/**
 * One place that turns a thrown thing into something a prospect can read.
 *
 * All three screens need this and none of them should decide it. The sentence comes from the server,
 * because `guest-failures.ts` wrote it for somebody who has clicked a link out of their inbox and is
 * not yet sure any of this is real - it is explicit that nothing on this surface says "invalid",
 * "denied" or "forbidden". A component that composed its own copy would quietly discard that care.
 *
 * The correlation id is rendered rather than logged. Section 13 puts one in every response because it
 * is the only thing a customer can read back to us over the phone, and a person on a phone has no
 * console to open.
 */
import { Notice } from '@iqz-ddds/components';
import { ApiError } from '../api/client';

export interface FailureNoticeProps {
  /** Whatever was caught. `null` renders nothing, so a caller needs no conditional of its own. */
  readonly error: unknown;
}

export function FailureNotice({ error }: FailureNoticeProps): React.JSX.Element | null {
  if (error === null || error === undefined) return null;

  const detail =
    error instanceof Error ? error.message : 'Something went wrong. Try again in a moment.';
  const reference = error instanceof ApiError ? error.reference : undefined;

  return (
    <Notice tone="error" {...(reference === undefined ? {} : { reference })}>
      {detail}
    </Notice>
  );
}
